import { Filter } from '@vrplatform/components';
import { useInfiniteQuery, useTeam } from '@vrplatform/data';
import { InfiniteTable, type MRT_SortingState } from '@vrplatform/table';
import type { change_bool_exp } from '@vrplatform/graphql';
import type { getActionMessage } from '@vrplatform/ui-common';
import { useState } from 'react';
import { useSyncedFromToTableColumns } from '../_hooks';
import type { WorkflowChange } from '../_types';
import { getWorkflowChangeRow } from '../_utils';

export const SyncedFromTable = ({
  workflowId,
  isWorkflowPending,
  overwrites,
}: {
  workflowId: string;
  isWorkflowPending: boolean;
  overwrites: Parameters<typeof getActionMessage>[1];
}) => {
  const [{ id: teamId }] = useTeam();
  const [sorting, setSorting] = useState<MRT_SortingState>([]);
  const [search, setSearch] = useState('');

  const columns = useSyncedFromToTableColumns(overwrites);

  const infiniteData = useInfiniteQuery(
    (q, { workflowId, search, teamId }, { limit, offset }) => {
      if (!workflowId)
        return {
          aggregate: 0,
          list: [],
        };

      const whereChange: change_bool_exp = {
        syncId: { _eq: workflowId },
        syncType: { _eq: 'pull' },
        message: search ? { _ilike: `%${search}%` } : undefined,
        tenantId: { _eq: teamId },
      };

      const aggregate =
        q.changeAggregate({ where: whereChange }).aggregate?.count() || 0;

      const list = q
        .changes({
          where: whereChange,
          limit,
          offset,
          order_by: [{ createdAt: 'desc' }],
        })
        .map<WorkflowChange>((change) =>
          getWorkflowChangeRow(change, 'synced-from')
        );

      return {
        aggregate,
        list,
      };
    },
    {
      variables: {
        search: search.trim(),
        workflowId,
        sorting,
        teamId,
      },
      queryKey: 'jobPlans',
      poll: {
        isCompleted: () => isWorkflowPending === false,
        interval: 5000,
      },
    }
  );

  const resetFilter = () => {
    setSearch('');
  };

  return (
    <InfiniteTable
      columns={columns}
      table={{
        // columns,
        hideHeader: true,
        emptyRowsFallback: 'Nothing was synced here.',
      }}
      queryData={infiniteData}
      sorting={{
        setSorting,
        sorting,
      }}
      resetFilter={resetFilter}
    >
      <Filter.Search value={search} setValue={setSearch} />
    </InfiniteTable>
  );
};
